import { SyncEnvelope, SyncResult, SyncAdapterConfig } from '../types/sync';
import { WorkspaceProfile, ApplicabilityDecision, AuditEntry } from '../types/decision';
import {
  getDecisions,
  getAuditTrail,
  bulkSaveDecisions,
  saveWorkspace,
  saveSyncConfig,
  getCustomSystems,
  saveCustomSystem,
  getMasterData,
  saveMasterData
} from './indexedDbStorage';

export async function computeDigest(data: any): Promise<string> {
  const jsonStr = JSON.stringify(data, Object.keys(data).sort());
  const encoder = new TextEncoder();
  const buffer = encoder.encode(jsonStr);
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

export async function createSyncEnvelope(workspace: WorkspaceProfile): Promise<SyncEnvelope> {
  const decisions = await getDecisions(workspace.id);
  const audit_trail = await getAuditTrail(workspace.id);
  const systems = await getCustomSystems(workspace.id);
  const master_data = await getMasterData(workspace.id);
  const snapshot_digest = await computeDigest(decisions);

  let clientId = localStorage.getItem('dpdp_client_id');
  if (!clientId) {
    clientId = `client_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    localStorage.setItem('dpdp_client_id', clientId);
  }

  return {
    schema_version: '1.0.0',
    workspace,
    client_id: clientId,
    snapshot_digest,
    last_synced_at: new Date().toISOString(),
    rev: Object.keys(decisions).length,
    decisions,
    systems,
    master_data,
    audit_trail
  };
}

export async function importSyncEnvelope(envelope: SyncEnvelope): Promise<{ success: boolean; count: number; message: string }> {
  if (!envelope || envelope.schema_version !== '1.0.0') {
    return { success: false, count: 0, message: 'Invalid or unsupported sync envelope version' };
  }

  // Validate digest
  const computed = await computeDigest(envelope.decisions);
  if (envelope.snapshot_digest && computed !== envelope.snapshot_digest) {
    console.warn('Digest mismatch during import; proceeding with import.');
  }

  await saveWorkspace(envelope.workspace);
  await bulkSaveDecisions(envelope.workspace.id, envelope.decisions);

  if (envelope.systems && Array.isArray(envelope.systems)) {
    for (const sys of envelope.systems) {
      await saveCustomSystem(envelope.workspace.id, sys);
    }
  }

  if (envelope.master_data) {
    await saveMasterData(envelope.workspace.id, envelope.master_data);
  }

  return {
    success: true,
    count: Object.keys(envelope.decisions).length,
    message: `Successfully imported ${Object.keys(envelope.decisions).length} decision records for "${envelope.workspace.name}"`
  };
}

export async function syncWithByodEndpoint(
  config: SyncAdapterConfig,
  workspace: WorkspaceProfile
): Promise<SyncResult> {
  if (!config.endpoint_url) {
    return {
      success: false,
      message: 'No BYOD endpoint URL configured',
      timestamp: new Date().toISOString()
    };
  }

  try {
    const envelope = await createSyncEnvelope(workspace);
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (config.api_key) {
      headers['Authorization'] = `Bearer ${config.api_key}`;
    } else if (config.auth_header) {
      headers['Authorization'] = config.auth_header;
    }

    const res = await fetch(config.endpoint_url, {
      method: 'POST',
      headers,
      body: JSON.stringify(envelope)
    });

    if (!res.ok) {
      const errText = await res.text();
      return {
        success: false,
        message: `HTTP ${res.status}: ${errText.slice(0, 100) || res.statusText}`,
        timestamp: new Date().toISOString()
      };
    }

    const responseData = await res.json().catch(() => ({}));
    const timestamp = new Date().toISOString();

    await saveSyncConfig({
      ...config,
      last_successful_sync: timestamp
    });

    return {
      success: true,
      message: 'Successfully synchronized decisions with BYOD backend',
      synced_count: Object.keys(envelope.decisions).length,
      remote_rev: responseData.rev,
      timestamp
    };
  } catch (err: any) {
    return {
      success: false,
      message: err?.message || 'Network error connecting to BYOD backend',
      timestamp: new Date().toISOString()
    };
  }
}
